"use client";

import { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

import { Card, CardContent } from "../ui/Card";
import { Slider } from "../ui/Slider";

const BASE_CARS = 30;
const BASE_EARNINGS = {
  year1: 40,
  year2: 55,
  year3: 86,
};
const MIN_CARS = 10;
const MAX_CARS = 50;
const MAX_EARNINGS_Y_AXIS = 150;

const calculateEarnings = (cars: number) => {
  const multiplier = cars / BASE_CARS;
  return [
    { name: "Year 1", earnings: Math.round(BASE_EARNINGS.year1 * multiplier) },
    { name: "Year 2", earnings: Math.round(BASE_EARNINGS.year2 * multiplier) },
    { name: "Year 3", earnings: Math.round(BASE_EARNINGS.year3 * multiplier) },
  ];
};

const CustomDot = (props: any) => {
  const { cx, cy, stroke, payload, index, data } = props;
  const isLastPoint = index === data.length - 1;

  const dot = <circle cx={cx} cy={cy} r={5} stroke={stroke} fill={stroke} strokeWidth={2} />;

  if (isLastPoint) {
    return (
      <g>
        {dot}
        <foreignObject x={cx - 50} y={cy - 80} width="100" height="60">
          <div
            className="flex flex-col items-center justify-center rounded-lg bg-white p-3 shadow-[0_4px_12px_rgba(0,0,0,0.15)] border border-gray-200"
          >
            <p className="text-xs font-semibold text-gray-600 mb-1">Year 3</p>
            <p className="text-sm font-bold text-gray-800">{payload.earnings} Lakhs</p>
          </div>
        </foreignObject>
      </g>
    );
  }

  return dot;
};

const EarningsCalculator = () => {
    const [carsPerMonth, setCarsPerMonth] = useState(BASE_CARS);
    const chartData = calculateEarnings(carsPerMonth);

    const year3Earnings = chartData[2].earnings;

    return (
        <section className="bg-white py-20">
            <div className="container mx-auto max-w-6xl px-4">
                <h2 className="text-center font-semibold text-dark-blue mb-8" style={{ fontSize: '32px', lineHeight: '1.3' }}>
                    Calculate your net earnings
                </h2>
                <Card className="rounded-lg border border-border-gray p-8 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
                    <CardContent className="p-0">
                        <p className="mb-8 text-text-secondary" style={{ fontSize: '18px' }}>
                            Yearly earnings based on {carsPerMonth} cars per month
                        </p>
                        <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
                            <div className="h-80 w-full lg:w-[55%]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={chartData}
                                        margin={{ top: 30, right: 30, left: -20, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border-gray)" />
                                        <XAxis
                                            dataKey="name"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: "var(--color-text-secondary)", fontSize: 14 }}
                                            dy={10}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            domain={[0, MAX_EARNINGS_Y_AXIS]}
                                            ticks={[25, 50, 75, 100]}
                                            tickFormatter={(value) => `${value}L`}
                                            tick={{ fill: "var(--color-text-secondary)", fontSize: 14 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="earnings"
                                            stroke="var(--color-primary)"
                                            strokeWidth={2}
                                            dot={(props: any) => <CustomDot {...props} data={chartData} />}
                                            activeDot={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="flex w-full flex-col lg:w-[45%] lg:pl-16">
                                <p className="font-semibold text-dark-blue" style={{ fontSize: '20px' }}>
                                    Potential Earnings in Year 3
                                </p>
                                <p className="my-4 font-bold text-dark-blue" style={{ fontSize: '48px', lineHeight: '1.2' }}>
                                    ₹{year3Earnings} Lakhs
                                </p>
                                <div className="mb-6 w-fit rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white">
                                    {carsPerMonth} cars/m
                                </div>
                                <Slider
                                    value={carsPerMonth}
                                    onChange={setCarsPerMonth}
                                    min={MIN_CARS}
                                    max={MAX_CARS}
                                    step={1}
                                    className="w-full max-w-xs"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </section>
    );
};

export default EarningsCalculator;
